// import instance from "./axiosInstance";
import axios from "axios";
import { api } from "./api_route";

/**
 * CLAIMANT DETAILS API
 * Handles all claimant-related API operations
 */

// ============================================================================
// CLAIMANT DETAILS OPERATIONS
// ============================================================================

/**
 * Create a new claimant
 * @param {Object} claimantData - Claimant details object
 * @returns {Promise}
 */
export const createClaimantDetailsAPI = async (claimantData) => {
  try {
    const config = {};
    if (typeof FormData !== "undefined" && claimantData instanceof FormData) {
      config.headers = { "Content-Type": "multipart/form-data" };
    }

    const response = await axios.post(
      `${api}/claimantDetails/create`,
      claimantData,
      config,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Update claimant details
 * @param {number} claimantDetailsID - Claimant ID
 * @param {Object} claimantData - Updated claimant details
 * @returns {Promise}
 */
export const updateClaimantDetailsAPI = async (
  claimantDetailsID,
  claimantData,
) => {
  try {
    const config = {};
    if (typeof FormData !== "undefined" && claimantData instanceof FormData) {
      config.headers = { "Content-Type": "multipart/form-data" };
    }

    const response = await axios.put(
      `${api}/claimantDetails/update/${claimantDetailsID}`,
      claimantData,
      config,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Delete claimant details (soft delete)
 * @param {number} claimantDetailsID - Claimant ID
 * @returns {Promise}
 */
export const deleteClaimantDetailsAPI = async (claimantDetailsID) => {
  try {
    const response = await axios.delete(
      `${api}/claimantDetails/delete/${claimantDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get claimant details by ID
 * @param {number} claimantDetailsID - Claimant ID
 * @returns {Promise}
 */
export const getClaimantDetailsAPI = async (claimantDetailsID) => {
  try {
    const response = await axios.get(
      `${api}/claimantDetails/get/${claimantDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get all claimants for a specific case
 * @param {number} caseID - Case ID
 * @returns {Promise}
 */
export const getClaimantsByCaseIDAPI = async (caseID) => {
  try {
    const response = await axios.get(`${api}/claimantDetails/case/${caseID}`);
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

// ============================================================================
// CLAIMANT DOCUMENTS OPERATIONS
// ============================================================================

/**
 * Upload claimant documents
 * @param {FormData} formData - FormData with file and metadata
 * @returns {Promise}
 */
export const uploadClaimantDocumentAPI = async (formData) => {
  try {
    const response = await axios.post(
      `${api}/claimantDetails/document/create`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Update claimant document
 * @param {number} documentID - Document ID
 * @param {Object} documentData - Document metadata to update
 * @returns {Promise}
 */
export const updateClaimantDocumentAPI = async (documentID, documentData) => {
  try {
    const response = await axios.put(
      `${api}/claimantDetails/document/update/${documentID}`,
      documentData,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Delete claimant document (soft delete)
 * @param {number} documentID - Document ID
 * @returns {Promise}
 */
export const deleteClaimantDocumentAPI = async (documentID) => {
  try {
    const response = await axios.delete(
      `${api}/claimantDetails/document/delete/${documentID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

/**
 * Get claimant documents
 * @param {number} claimantDetailsID - Claimant ID
 * @returns {Promise}
 */
export const getClaimantDocumentsAPI = async (claimantDetailsID) => {
  try {
    const response = await axios.get(
      `${api}/claimantDetails/documents/${claimantDetailsID}`,
    );
    return {
      data: response.data,
      status: response.status,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.message || error.message,
      status: error.response?.status,
      success: false,
    };
  }
};

export const claimantDocumentDownload = async (values) => {
  try {
    const response = await axios.get(
      `${api}/documents/claimantDocuments/${values.original.documentNewName}`,
      {
        responseType: "blob",
      },
    );

    return response.data;
  } catch (error) {
    return [];
  }
};

// Document Categories Constants
export const CLAIMANT_DOCUMENT_CATEGORIES = {
  AADHAAR_ATTACHMENT: 1237,
  PAN_ATTACHMENT: 1238,
  CIN_GSTIN: 1239,
  INCORPORATE_CERTIFICATE: 1240,
  OTHERS: 1241,
};

export const DOCUMENT_CATEGORY_LABELS = {
  1237: "Aadhaar Attachment",
  1238: "PAN Attachment",
  1239: "CIN/GSTIN",
  1240: "Incorporate Certificate",
  1241: "Others",
};
